import { AnthropicApiError } from './types';
import type {
  AnthropicUsageData,
  AnthropicCostBucket,
  AnthropicApiKey,
  AnthropicWorkspace,
  AnthropicUser,
  AnthropicApiKeysParams,
  UsageReportParams,
  CostReportParams
} from './types';
import { retryDelayMs, MAX_PROVIDER_WAIT_MS } from '../http-retry';
import {
  RETRYABLE_ANTHROPIC_STATUS_CODES,
  MAX_RETRIES,
  BASE_DELAY
} from './constants';
import {
  fetchAnthropicUsageReport,
  fetchAnthropicCostReport,
  fetchAnthropicApiKeys,
  fetchAnthropicWorkspaces,
  fetchAnthropicUsers
} from './client';
import { debugLog } from '../debug-log';


/**
 * Sleep helper for rate limiting
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetch all API keys (handles pagination)
 */
export async function fetchAllAnthropicApiKeys(
  status: 'active' | 'inactive' | 'all' = 'active'
): Promise<AnthropicApiKey[]> {
  const allKeys: AnthropicApiKey[] = [];
  const cursors = new Set<string>();
  let hasMore = true;
  let afterId: string | undefined;

  while (hasMore) {
    const params: AnthropicApiKeysParams = {
      limit: 100,
      after_id: afterId
    };

    if (status !== 'all') {
      params.status = status;
    }

    const response = await fetchAnthropicApiKeys(params);
    allKeys.push(...response.data);
    hasMore = response.has_more;
    const newAfterId = response.last_id ?? undefined;

    if (hasMore && (!newAfterId || cursors.has(newAfterId) || cursors.size >= 1000)) {
      throw new Error('Anthropic API key pagination did not complete');
    }
    if (newAfterId) cursors.add(newAfterId);
    afterId = newAfterId;

    if (hasMore) {
      await sleep(500);
    }
  }

  return allKeys;
}

export async function fetchAllAnthropicWorkspaces(
  includeArchived = false
): Promise<AnthropicWorkspace[]> {
  const allWorkspaces: AnthropicWorkspace[] = [];
  const cursors = new Set<string>();
  let hasMore = true;
  let afterId: string | undefined;

  while (hasMore) {
    const params: { include_archived?: boolean; limit?: number; after_id?: string } = {
      include_archived: includeArchived,
      limit: 100
    };
    if (afterId) {
      params.after_id = afterId;
    }

    const response = await fetchAnthropicWorkspaces(params);
    allWorkspaces.push(...response.data);
    hasMore = response.has_more;
    const newAfterId = response.last_id ?? undefined;

    if (hasMore && (!newAfterId || cursors.has(newAfterId) || cursors.size >= 1000)) {
      throw new Error('Anthropic workspace pagination did not complete');
    }
    if (newAfterId) cursors.add(newAfterId);
    afterId = newAfterId;

    if (hasMore) {
      await sleep(500);
    }
  }

  return allWorkspaces;
}

/**
 * Fetch all organization users (handles pagination)
 */
export async function fetchAllAnthropicUsers(): Promise<AnthropicUser[]> {
  const allUsers: AnthropicUser[] = [];
  const cursors = new Set<string>();
  let hasMore = true;
  let afterId: string | undefined;

  while (hasMore) {
    const params: { limit: number; after_id?: string } = { limit: 100 };
    if (afterId) {
      params.after_id = afterId;
    }

    const response = await fetchAnthropicUsers(params);
    allUsers.push(...response.data);
    hasMore = response.has_more;
    const newAfterId = response.last_id ?? undefined;

    if (hasMore && (!newAfterId || cursors.has(newAfterId) || cursors.size >= 1000)) {
      throw new Error('Anthropic user pagination did not complete');
    }
    if (newAfterId) cursors.add(newAfterId);
    afterId = newAfterId;

    if (hasMore) {
      await sleep(500);
    }
  }

  return allUsers;
}

/**
 * Fetch all usage data with pagination
 */
export async function fetchAllAnthropicUsage(
  startDate: Date,
  endDate: Date,
  bucketWidth: '1m' | '1h' | '1d' = '1d',
  groupBy?: UsageReportParams['group_by']
): Promise<AnthropicUsageData[]> {
  const allData: AnthropicUsageData[] = [];
  let hasMore = true;
  let nextPage: string | undefined;
  let retryCount = 0;

  // Anthropic API max limits: 1d=31, 1h=168, 1m=1440
  const limit = bucketWidth === '1h' ? 168 : bucketWidth === '1m' ? 1440 : 31;

  const params: UsageReportParams = {
    starting_at: startDate.toISOString().replace('.000Z', 'Z'),
    ending_at: endDate.toISOString().replace('.000Z', 'Z'),
    bucket_width: bucketWidth,
    group_by: groupBy,
    limit
  };

  while (hasMore) {
    if (nextPage) {
      params.page = nextPage;
      // Short delay between pagination pages (burst-acceptable per Anthropic docs)
      await sleep(300);
    }

    try {
      const response = await fetchAnthropicUsageReport(params);
      debugLog('[Anthropic Usage] Response', {
        bucketWidth,
        page: params.page ?? 'first',
        buckets: response.data.length,
        hasMore: response.has_more,
        nextPage: response.next_page
      });
      // Flatten the nested structure - each bucket contains results array
      response.data.forEach(bucket => {
        // Add timestamp to each result item from the bucket's starting_at
        const bucketData = bucket.results.map(item => ({
          ...item,
          timestamp: item.timestamp || bucket.starting_at
        }));
        allData.push(...bucketData);
      });
      hasMore = response.has_more;
      const newNextPage = response.next_page;
      if (hasMore && newNextPage === nextPage) {
        console.warn('[Anthropic Usage] Pagination cursor not advancing, breaking to prevent infinite loop');
        break;
      }
      nextPage = newNextPage;
      retryCount = 0; // Reset retry count on success
    } catch (error: unknown) {
      // Handle retryable errors (429, 5xx, 529)
      const isRetryable = error instanceof AnthropicApiError &&
        RETRYABLE_ANTHROPIC_STATUS_CODES.has(error.status);

      if (isRetryable && retryCount < MAX_RETRIES) {
        retryCount++;
        // Use Retry-After header if available, otherwise exponential backoff
        const retryAfter = error.retryAfterMs;
        const jitter = retryDelayMs(retryAfter ?? null, BASE_DELAY * Math.pow(2, retryCount));
        if (jitter > MAX_PROVIDER_WAIT_MS) throw new Error(`Retry deferred: provider requires waiting ${Math.ceil(jitter / 1000)} seconds`);
        console.warn(`[Anthropic API] ${error.status} error. Retrying in ${Math.round(jitter)}ms (attempt ${retryCount}/${MAX_RETRIES})`);
        await sleep(jitter);
        continue;
      }
      console.error('Error fetching Anthropic usage data:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch Anthropic usage data');
    }
  }

  return allData;
}

/**
 * Fetch all cost data with pagination
 * Note: Cost API only supports daily granularity (1d)
 */
export async function fetchAllAnthropicCosts(
  startDate: Date,
  endDate: Date,
  groupBy?: ('workspace_id' | 'description')[]
): Promise<AnthropicCostBucket[]> {
  const allData: AnthropicCostBucket[] = [];
  let hasMore = true;
  let nextPage: string | undefined;
  let retryCount = 0;

  // Cost API only supports daily granularity
  const params: CostReportParams = {
    starting_at: startDate.toISOString().replace('.000Z', 'Z'),
    ending_at: endDate.toISOString().replace('.000Z', 'Z'),
    group_by: groupBy,
    limit: 31  // Anthropic Cost API has a max limit of 31
  };

  while (hasMore) {
    if (nextPage) {
      params.page = nextPage;
      await sleep(300);
    }

    try {
      const response = await fetchAnthropicCostReport(params);
      allData.push(...response.data);
      hasMore = response.has_more;
      const newNextPage = response.next_page;
      if (hasMore && newNextPage === nextPage) {
        console.warn('[Anthropic Cost] Pagination cursor not advancing, breaking to prevent infinite loop');
        break;
      }
      nextPage = newNextPage;
      retryCount = 0;
    } catch (error: unknown) {
      const isRetryable = error instanceof AnthropicApiError &&
        RETRYABLE_ANTHROPIC_STATUS_CODES.has(error.status);

      if (isRetryable && retryCount < MAX_RETRIES) {
        retryCount++;
        const retryAfter = error.retryAfterMs;
        const jitter = retryDelayMs(retryAfter ?? null, BASE_DELAY * Math.pow(2, retryCount));
        if (jitter > MAX_PROVIDER_WAIT_MS) throw new Error(`Retry deferred: provider requires waiting ${Math.ceil(jitter / 1000)} seconds`);
        console.warn(`[Anthropic Cost API] ${error.status} error. Retrying in ${Math.round(jitter)}ms (attempt ${retryCount}/${MAX_RETRIES})`);
        await sleep(jitter);
        continue;
      }
      console.error('Error fetching Anthropic cost data:', error);
      throw error instanceof Error ? error : new Error('Failed to fetch Anthropic cost data');
    }
  }

  return allData;
}
